import React, { useEffect, useMemo, useState } from "react";
import {
  fieldTypeOptions,
  formatSelectOptionsFromServer,
} from "../GenericField";
import { GET_QUERY_FIELD } from "../../TicketingAPI";
import { useLazyQuery } from "@apollo/client";
import { useWatch, type Control, type FieldValues } from "react-hook-form";
import {
  SelectInput,
  type SelectOption as SharedSelectOption,
} from "@SeemplicityDev/shared";

interface SelectOption {
  // `value`/`label` are optional to align with `FieldSelectOption` returned by
  // `formatSelectOptionsFromServer` (server ids/names may be absent).
  value?: unknown;
  label?: unknown;
}

interface ServerOption {
  id?: string;
  name?: string;
}

type LoadOptionsCallback = (options: SelectOption[]) => void;

interface SearchQueryFieldProps {
  control?: Control<FieldValues>;
  input?: string;
  name?: string;
  params?: string[];
  // preLoadedOptions are options that are delivered from the server initially
  // This is necessary for saved keys / fields
  options?: ServerOption[];
  value?: unknown;
  creatable?: boolean;
  classname?: string;
  label?: string;
  onChange?: (value: unknown) => void;
  required?: boolean;
  onBlur?: () => void;
  disabled?: boolean;
  error?: string | null;
  clearable?: boolean;
  onSelectedOptionObjectChange?: (
    name: string | undefined,
    option: SelectOption,
  ) => void;
  viewMode?: boolean;
}

const SearchQueryField: React.FC<SearchQueryFieldProps> = ({
  control,
  input,
  name,
  params = [],
  // preLoadedOptions are options that are delivered from the server initially
  // This is necessary for saved keys / fields
  options: preLoadedOptions = [],
  value,
  creatable,
  classname,
  label,
  onChange,
  required,
  onBlur,
  disabled,
  error,
  clearable,
  onSelectedOptionObjectChange = () => {},
  viewMode = false,
}) => {
  // Storing the option object is necessary because the value held by SelectInput
  // should be the complete option object. However, the value prop in this component
  // only accommodates the value of the option object, and we can't search for it in options
  // because it is AsyncSelect, so we don't hold the options array.
  const [selectedOptionObject, setSelectedOptionObject] =
    useState<SelectOption | null>(null);
  const [dynamicOptions, setDynamicOptions] = useState<SelectOption[]>();

  const [getSearchQueryFieldOptions] = useLazyQuery(GET_QUERY_FIELD);

  useEffect(() => {
    if (!!selectedOptionObject) {
      onSelectedOptionObjectChange(name, selectedOptionObject);
    }
  }, [selectedOptionObject]);

  // This effect handles the scenario where the value is externally retested
  // after a user interaction was performed.
  useEffect(() => {
    if (!value && !!selectedOptionObject) {
      setSelectedOptionObject(null);
    }
  }, [value]);

  // This effect handles the scenario where the value is externally provided
  // rather than being a result of user interaction.
  // necessary for editing or loading this field value.
  useEffect(() => {
    let selectedOption: SelectOption | undefined;
    // Verify the presence of preloaded options and confirm that a non-user-initiated value has been injected.
    if (value) {
      if (preLoadedOptions?.length) {
        selectedOption = formatSelectOptionsFromServer(preLoadedOptions)?.find(
          (option: SelectOption) => option?.value === value,
        );
      }

      if (!selectedOption && dynamicOptions?.length) {
        selectedOption = dynamicOptions?.find(
          (option) => option?.value === value,
        );
      }

      // Find and set the matching option object based on the provided value
      if (!!selectedOption) {
        setSelectedOptionObject(selectedOption);
      }
    }
  }, [preLoadedOptions, dynamicOptions, value]);

  const selectedKeys = useWatch({
    control,
    name: `keys`,
  });

  const selectedFields = useWatch({
    control,
    name: `fields`,
  });

  const selectedTicketProvider = useWatch({
    control,
    name: `ticketProvider`,
  });

  const paramsValues = useMemo(() => {
    let obj: Record<string, unknown> = {};
    if (!!params) {
      obj = params?.reduce(
        (acc, param) => ({
          ...acc,
          [param]: selectedKeys?.[param] || selectedFields?.[param],
        }),
        {},
      );
    }

    return obj;
  }, [params, selectedFields, selectedKeys]);

  const getFieldPromiseOptions = (
    inputValue: string,
    callback: LoadOptionsCallback,
  ) => {
    if (!name || !selectedTicketProvider) {
      callback([]);
    }

    getSearchQueryFieldOptions({
      variables: {
        params: JSON.stringify({
          ...paramsValues,
          search_string: inputValue,
          limit: 10,
        }),
        field_name: name,
        ticket_provider_id: selectedTicketProvider,
      },
    })
      .then((res) => {
        const newOptions = formatSelectOptionsFromServer(
          res?.data?.get_query_fields?.values,
        );
        setDynamicOptions(newOptions);

        callback(res ? (newOptions ?? []) : []);
      })
      .catch(() => {
        callback([]);
      });
  };

  switch (input) {
    case fieldTypeOptions.SELECT:
      return (
        <SelectInput
          wrapperClassName={`generic-field generic-field-select ${classname}`}
          data-testid={name}
          noMargin
          name={name}
          // Local SelectOption has unknown value/label; SelectInput narrows to
          // its SelectValue. Runtime shape is compatible.
          value={selectedOptionObject as SharedSelectOption | null}
          viewMode={viewMode}
          isAsync
          defaultOptions={
            (formatSelectOptionsFromServer(preLoadedOptions) || true) as
              | boolean
              | SharedSelectOption[]
          }
          // loadOptions invokes a node-style callback at runtime; SelectInput's
          // declared signature omits the callback param but supplies it.
          loadOptions={
            getFieldPromiseOptions as unknown as React.ComponentProps<
              typeof SelectInput
            >["loadOptions"]
          }
          isCreatable={creatable}
          debounceTime={1000}
          disabled={disabled}
          onBlur={onBlur}
          labelTop={!!label}
          setDefaultSingleOption={false}
          onChange={(val: SelectOption) => {
            onChange?.(val?.value);
          }}
          // `error` is a string-or-null flag here; SelectInput narrows to an
          // error object. Preserve the original runtime value via a cast.
          error={
            error as unknown as React.ComponentProps<typeof SelectInput>["error"]
          }
          isClearable={clearable}
          placeholder={"Start typing what you're looking for..."}
          isSearchable
          label={label}
          required={required}
        />
      );
    default:
      return <></>;
  }
};

export default SearchQueryField;
